/**
 * "Mass DM filter preset (regular chatters)" — in de huidige bundle een
 * dialoog naast de tabbalk van Chatters, en hetzelfde formulier onder
 * Settings → General.
 *
 * Vaste voorinstelling voor chatters die de filters zelf niet mogen zetten:
 * managers regelen het hier, zonder Settings-toegang. Per-persoon-uitzon-
 * deringen blijven onder Edit chatter / Create account.
 *
 * 30-10-2026 (Jordy): leest en schrijft nu de instelling die de server bij
 * het verzenden echt afdwingt, `system_settings.chatter_floor_exclusions`
 * (GET/PUT /api/admin/dashboard/chatter-floor-exclusions;
 * src/api/chatter_floor_exclusions.py). Daarvoor schreef dit scherm de losse
 * sleutel `mass_dm_filter_preset`, die de verzendroute nooit las. De velden
 * heten in beide gelijk (private_chats, timewasters, spenders, new_followers,
 * online_only, exclude_recent_minutes, lock_filters_for_chatters); de oude
 * sleutel had geen veld zonder tegenhanger en blijft ongemoeid in de database.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useActions, mut } from '../lib/actions'
import { useApi } from '../lib/useApi'
import { ApiError } from '../lib/api'
import { ErrorNote, Loading } from './ui'

export const PRESET_PAD = '/api/admin/dashboard/chatter-floor-exclusions'

interface Segmenten {
  private_chats: boolean
  timewasters: boolean
  spenders: boolean
  new_followers: boolean
  online_only: boolean
}
export interface Floor extends Segmenten {
  exclude_recent_minutes: number
  lock_filters_for_chatters: boolean
}
interface FloorAntwoord {
  floor: Floor
  recent_minutes_choices?: number[]
}

const EXCLUDES: [keyof Segmenten, string][] = [
  ['private_chats', 'Private chats (special-need / claimed)'],
  ['timewasters', 'Timewasters'],
  ['spenders', 'Paying fans'],
  ['new_followers', 'New followers'],
  ['online_only', 'Online fans only'],
]
/** Als de server geen keuzes meestuurt (RECENT_MINUTES_CHOICES op de server). */
const RECENT_STANDAARD = [0, 10, 20, 30, 45, 60]

function recentLabel(m: number): string {
  if (m === 0) return "Don't exclude recent replies"
  if (m >= 60 && m % 60 === 0) return `Last ${m / 60} hour${m === 60 ? '' : 's'}`
  return `Last ${m} minutes`
}

/** Het formulier zelf. Opslaan kan pas als de opgeslagen waarden binnen zijn,
 *  zodat het nooit standaardwaarden over de afgedwongen waarden heen schrijft. */
export function MassDmPresetForm({ onSaved, extra }: { onSaved?: () => void; extra?: ReactNode }) {
  const opgeslagen = useApi<FloorAntwoord>(PRESET_PAD)
  const [floor, setFloor] = useState<Floor | null>(null)
  const { run, busy } = useActions()
  useEffect(() => {
    if (opgeslagen.data?.floor) setFloor({ ...opgeslagen.data.floor })
  }, [opgeslagen.data])

  const keuzes = Array.from(new Set([
    ...(opgeslagen.data?.recent_minutes_choices ?? RECENT_STANDAARD),
    // Een opgeslagen waarde buiten de lijst blijft zichtbaar en kiesbaar.
    ...(floor ? [floor.exclude_recent_minutes] : []),
  ])).sort((a, b) => a - b)
  const geenToegang = opgeslagen.error instanceof ApiError && opgeslagen.error.status === 403

  let inhoud: ReactNode
  if (geenToegang) {
    inhoud = (
      <div className="mut2" style={{ fontSize: 12, marginTop: 8 }} data-testid="preset-geen-toegang">
        You can't change the preset. Owners, admins and managers with the Mass Messages page can.
      </div>
    )
  } else if (opgeslagen.error && !floor) {
    inhoud = <ErrorNote error={opgeslagen.error} />
  } else if (!floor) {
    inhoud = <Loading />
  } else {
    inhoud = (
      <>
        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>
          Always exclude for locked chatters
        </div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {EXCLUDES.map(([key, label]) => (
            <label className="chkrow" key={key} data-testid={`preset-${key}`}>
              <span
                className={floor[key] ? 'chk on' : 'chk'}
                onClick={() => setFloor((f) => (f ? { ...f, [key]: !f[key] } : f))}
              >
                {floor[key] ? '✓' : ''}
              </span>
              {label}
            </label>
          ))}
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <label>Exclude recent replies (preset)</label>
          <select
            className="inp"
            data-testid="preset-recent"
            value={floor.exclude_recent_minutes}
            onChange={(e) => {
              const m = Number(e.target.value)
              setFloor((f) => (f ? { ...f, exclude_recent_minutes: m } : f))
            }}
          >
            {keuzes.map((m) => (
              <option key={m} value={m}>{recentLabel(m)}</option>
            ))}
          </select>
        </div>

        <label className="chkrow" data-testid="preset-lock">
          <span
            className={floor.lock_filters_for_chatters ? 'chk on' : 'chk'}
            onClick={() => setFloor((f) => (f ? { ...f, lock_filters_for_chatters: !f.lock_filters_for_chatters } : f))}
          >
            {floor.lock_filters_for_chatters ? '✓' : ''}
          </span>
          Hide filters from chatters (use preset only)
        </label>

        <div className="note" style={{ marginTop: 10 }} data-testid="preset-uitleg">
          The server applies this preset whenever a chatter sends a Mass DM
          from Chats (text or media): every filter the chatter has no
          per-person unlock for gets the value above. With "Hide filters" off,
          all filters are free. Automatic Mass Message proposals copy these
          filters when they are generated. Messages a chatter schedules on the
          Mass Messages page keep the filters they were scheduled with.
        </div>
      </>
    )
  }

  return (
    <>
      {inhoud}
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 10 }}>
        {extra}
        <button
          className="btn primary sm"
          type="button"
          data-testid="preset-opslaan"
          disabled={!floor || geenToegang || busy === 'Save preset'}
          onClick={() => {
            if (!floor) return
            run({
              label: 'Save preset',
              // Altijd alle zeven velden: een ontbrekend veld vult de server
              // met zijn eigen standaard.
              request: mut.put(PRESET_PAD, {
                private_chats: floor.private_chats,
                timewasters: floor.timewasters,
                spenders: floor.spenders,
                new_followers: floor.new_followers,
                online_only: floor.online_only,
                exclude_recent_minutes: floor.exclude_recent_minutes,
                lock_filters_for_chatters: floor.lock_filters_for_chatters,
              }),
              onDone: () => {
                opgeslagen.refetch()
                onSaved?.()
              },
              successText: 'Preset saved.',
            })
          }}
        >
          Save preset
        </button>
      </div>
    </>
  )
}

export function MassDmPresetDialog({ onClose }: { onClose: () => void }) {
  return (
    <div
      // Niet sluiten bij een klik op de achtergrond; zie Modal.tsx (27-09). Sluiten via Close.
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 99998,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="card" style={{ width: 520, maxWidth: 'calc(94vw / var(--dz, 1))' }}>
        <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>
          Mass DM filter preset (regular chatters)
        </div>
        <div className="mut2" style={{ fontSize: 12, marginBottom: 12 }}>
          Agency pre-setup for chatters who cannot edit filters. Managers set this
          here — no Settings access needed. Per-person unlocks stay under Edit
          chatter / Create account.
        </div>
        <MassDmPresetForm
          onSaved={onClose}
          extra={<button className="btn ghost sm" type="button" onClick={onClose}>Close</button>}
        />
      </div>
    </div>
  )
}
